import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import InputAdornment from '@mui/material/InputAdornment';
import AddRounded from '@mui/icons-material/AddRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import FileDownloadRounded from '@mui/icons-material/FileDownloadRounded';
import exportRowsToXlsx from '../utils/exportXlsx';
import FactoryRounded from '@mui/icons-material/FactoryRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import PageHeader from '../components/common/PageHeader';
import { getManufacturers, createManufacturer, updateManufacturer, deleteManufacturer } from '../services/manufacturersApi';
import { formatNumber, numberInputValue } from '../utils/formatNumber';
import { getCurrencies } from '../services/currenciesApi';
import { getCountries } from '../services/countriesApi';

const Manufacturers = () => {
  const [items, setItems] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: '', lead_time: '', currency_id: '', country_id: '', discount: '', markup: '' });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [importing, setImporting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [mfrs, curr, ctry] = await Promise.all([
        getManufacturers(),
        getCurrencies(),
        getCountries(),
      ]);
      setItems(mfrs.items);
      setCurrencies(curr.items);
      setCountries(ctry.items);
    } catch (err) {
      console.error('Failed to fetch manufacturers:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditItem(null);
    setForm({ name: '', lead_time: '', currency_id: '', country_id: '', discount: '', markup: '' });
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      name: item.name,
      lead_time: numberInputValue(item.lead_time),
      currency_id: item.currency_id || '',
      country_id: item.country_id || '',
      discount: numberInputValue(item.discount),
      markup: numberInputValue(item.markup),
    });
    setError('');
    setDialogOpen(true);
  };

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === 'name') setError('');
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name.trim(),
        lead_time: parseInt(form.lead_time) || 0,
        currency_id: form.currency_id || null,
        country_id: form.country_id || null,
        discount: parseFloat(form.discount) || 0,
        markup: parseFloat(form.markup) || 0,
      };
      if (editItem) {
        await updateManufacturer(editItem.id, payload);
      } else {
        await createManufacturer(payload);
      }
      setDialogOpen(false);
      await fetchData();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0]?.message;
      setError(msg || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this manufacturer?')) return;
    setDeleting(id);
    try {
      await deleteManufacturer(id);
      await fetchData();
    } catch (err) {
      console.error('Failed to delete:', err);
    } finally {
      setDeleting(null);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const XLSX = await import('xlsx');
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
      // Find the header row (look for 'name' in first cell)
      let headerIdx = 0;
      for (let i = 0; i < Math.min(rows.length, 5); i++) {
        if (String(rows[i][0] || '').toLowerCase() === 'name') { headerIdx = i; break; }
      }
      const headers = rows[headerIdx].map((h) => String(h || '').toLowerCase().trim());

      const currencyMap = {};
      currencies.forEach((c) => { currencyMap[c.name.toLowerCase()] = c.id; });
      const countryMap = {};
      countries.forEach((c) => { countryMap[c.name.toLowerCase()] = c.id; });
      const existingNames = new Set(items.map((i) => i.name.toLowerCase()));

      let created = 0, skipped = 0;
      for (let i = headerIdx + 1; i < rows.length; i++) {
        const row = {};
        headers.forEach((h, idx) => { row[h] = rows[i][idx]; });
        const name = String(row.name || '').trim();
        if (!name || existingNames.has(name.toLowerCase())) { skipped++; continue; }
        const leadTimeStr = String(row.leadtime || row['lead_time'] || '');
        const leadTime = parseInt(leadTimeStr) || 0;
        const currencyName = String(row.currency || '').trim().toLowerCase();
        const countryName = String(row.country || '').trim().toLowerCase();
        await createManufacturer({
          name,
          lead_time: leadTime,
          currency_id: currencyMap[currencyName] || null,
          country_id: countryMap[countryName] || null,
          discount: parseFloat(row.discount) || 0,
          markup: parseFloat(row.markup) || 0,
        });
        existingNames.add(name.toLowerCase());
        created++;
      }
      alert(`Import complete! ${created} created, ${skipped} skipped (empty or duplicate)`);
      await fetchData();
    } catch (err) {
      console.error('Import failed:', err);
      alert(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <Box>
        <PageHeader title="Manufacturers" subtitle="Manage manufacturers, pricing, and lead times" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Manufacturers"
        subtitle="Manage manufacturers, pricing, and lead times"
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <input id="manufacturers-import-file" type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleImport} />
            <Button variant="outlined" startIcon={<UploadFileRounded />}
              onClick={() => document.getElementById('manufacturers-import-file')?.click()}
              disabled={importing}>
              {importing ? 'Importing...' : 'Import Excel'}
            </Button>
            <Button variant="outlined" startIcon={<FileDownloadRounded />}
              onClick={() => {
                const currencyName = (id) => currencies.find((c) => c.id === id)?.name || '';
                const countryName = (id) => countries.find((c) => c.id === id)?.name || '';
                exportRowsToXlsx({
                  rows: items.map((i) => ({
                    name: i.name,
                    country: countryName(i.country_id),
                    currency: currencyName(i.currency_id),
                    lead_time: i.lead_time ?? 0,
                    discount: i.discount ?? 0,
                    markup: i.markup ?? 0,
                  })),
                  columns: ['name', 'country', 'currency', 'lead_time', 'discount', 'markup'],
                  filename: 'manufacturers',
                  sheetName: 'Manufacturers',
                });
              }}
              disabled={items.length === 0}>
              Export
            </Button>
            <Button variant="contained" startIcon={<AddRounded />} onClick={openCreate}>
              Add Manufacturer
            </Button>
          </Box>
        }
      />

      {items.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search manufacturers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ width: 300 }}
          />
        </Box>
      )}

      {items.length === 0 ? (
        <Card>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <FactoryRounded sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">No manufacturers added</Typography>
            <Typography variant="body2" color="text.secondary">Click "Add Manufacturer" to create your first entry</Typography>
          </Box>
        </Card>
      ) : (
        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Lead Time</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Currency</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Country</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Discount</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Markup</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary', width: 100 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.filter((item) => {
                  if (!searchQuery.trim()) return true;
                  const q = searchQuery.toLowerCase();
                  return item.name.toLowerCase().includes(q) ||
                    (item.country_name || '').toLowerCase().includes(q) ||
                    (item.currency_name || '').toLowerCase().includes(q);
                }).map((item) => (
                  <TableRow key={item.id} sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell><Typography variant="body2" sx={{ fontWeight: 500 }}>{item.name}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{formatNumber(item.lead_time)} days</Typography></TableCell>
                    <TableCell><Typography variant="body2">{item.currency_name || '--'}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{item.country_name || '--'}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{formatNumber(item.discount)}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{formatNumber(item.markup)}</Typography></TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(item)} sx={{ color: 'primary.main' }}>
                            <EditRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(item.id)} disabled={deleting === item.id} sx={{ color: 'error.main' }}>
                            {deleting === item.id ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineRounded fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{editItem ? 'Edit Manufacturer' : 'Add Manufacturer'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus label="Name" placeholder="e.g. Acme Furniture Co." value={form.name}
            onChange={(e) => updateForm('name', e.target.value)}
            fullWidth sx={{ mt: 1, mb: 2 }} error={!!error} helperText={error}
          />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Lead Time (Days)" type="number" value={form.lead_time}
              onChange={(e) => updateForm('lead_time', e.target.value)}
              fullWidth
            />
            <TextField
              select label="Currency" value={form.currency_id}
              onChange={(e) => updateForm('currency_id', e.target.value)}
              fullWidth
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {currencies.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              select label="Country" value={form.country_id}
              onChange={(e) => updateForm('country_id', e.target.value)}
              fullWidth
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {countries.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Discount" type="number" value={form.discount}
              onChange={(e) => updateForm('discount', e.target.value)}
              fullWidth
            />
            <TextField
              label="Markup" type="number" value={form.markup}
              onChange={(e) => updateForm('markup', e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : (editItem ? 'Save' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Manufacturers;
